import { yupResolver } from '@hookform/resolvers/yup'
import { getEPaymentMethodList } from '@if/core/types'
import {
  ControlledDateOnlyPicker,
  ControlledSelect,
  ControlledTextField,
  stopPropagationSubmitHandler,
  useTranslatedOptionEnum,
} from '@if/mui'
import { Grid } from '@mui/material'
import { EPayoutTarget } from '@src/types/constants'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import FormDialog from '../Common/Dialog/FormDialog'
import type { PayoutSchemaType } from './loan-form-schema'
import { PayoutSchema } from './loan-form-schema'

interface Props {
  payeeType: EPayoutTarget
  open: boolean
  onClose: () => void
  onSubmit: (data: PayoutSchemaType) => void
  defaultValues?: PayoutSchemaType
}

const PayoutDialog = ({ payeeType, open, onClose, onSubmit, defaultValues }: Props) => {
  const { t } = useTranslation()
  const { handleSubmit, reset, control, setValue } = useForm<PayoutSchemaType>({
    resolver: yupResolver(PayoutSchema),
    defaultValues: defaultValues ?? ({ ...PayoutSchema.getDefault() } as unknown as PayoutSchemaType),
  })

  const handleClose = () => {
    reset()
    onClose()
  }

  const onSubmitHandler = (data: PayoutSchemaType) => {
    reset()
    onClose()
    data.lines = defaultValues?.lines ?? []
    onSubmit(data)
  }

  const paymentMethodOptions = useTranslatedOptionEnum('EPaymentMethod', getEPaymentMethodList(), 'enum')
  const title = payeeType === EPayoutTarget.Merchant ? t('loan.addMerchant') : t('loan.addBorrower')

  useEffect(() => {
    setValue('payeeType', payeeType)
  })

  return (
    <FormDialog
      title={title}
      open={open}
      onClose={handleClose}
      onConfirm={stopPropagationSubmitHandler(handleSubmit(onSubmitHandler))}
    >
      <Grid container rowGap={2} columnGap={1}>
        <Grid size={12}>
          <ControlledTextField
            control={control}
            name="payeeId"
            label={payeeType === EPayoutTarget.Merchant ? t('loan.targetMerchant') : t('loan.targetBorrower')}
          />
        </Grid>
        <Grid size={12}>
          <ControlledSelect
            control={control}
            name="paymentMethod"
            label={t('loan.paymentMethod')}
            options={paymentMethodOptions}
          />
        </Grid>
        <Grid size={12}>
          <ControlledDateOnlyPicker control={control} name="paymentOn" label={t('loan.paymentOn')} />
        </Grid>
      </Grid>
    </FormDialog>
  )
}

export default PayoutDialog
